import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { reduxForm, SubmissionError } from 'redux-form';

import {
  createReport,
  updateReport,
} from 'redux/modules/reports';

import objectDiff from 'utils/objectDiff';
import * as validate from 'utils/validate';

import dateFormat from 'config/dateFormat';

import ReportsCreateEditFormAside from './ReportsCreateEditFormAside';
import ReportsCreateEditFormMain from './ReportsCreateEditFormMain';

const styles = theme => ({
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
});

function onSubmit(copyAndSaveAsNew, copyValues) {
  return (values, dispatch, props) => {
    const normalizeValues = (formValues) => {
      const normalizedValues = Object.assign({}, formValues, {
        ...formValues['Date of Service From']
          ? { 'Date of Service From': formValues['Date of Service From'] } : {},
        ...formValues['Date of Service From'] === ''
          ? { 'Date of Service From': null } : {},
        ...formValues['Date of Service To']
          ? { 'Date of Service To': formValues['Date of Service To'] } : {},
        ...formValues['Date of Service To'] === ''
          ? { 'Date of Service To': null } : {},
        ...formValues['Date of Creation From']
          ? { 'Date of Creation From': formValues['Date of Creation From'] } : {},
        ...formValues['Date of Creation From'] === ''
          ? { 'Date of Creation From': null } : {},
        ...formValues['Date of Creation To']
          ? { 'Date of Creation To': formValues['Date of Creation To'] } : {},
        ...formValues['Date of Creation To'] === ''
          ? { 'Date of Creation To': null } : {},
        ...formValues['Workflow State']
          ? { 'Workflow State ID': formValues['Workflow State'].map(item => item.value) } : {},
        ...formValues['Workflow Status']
          ? { 'Workflow Status ID': formValues['Workflow Status'].map(item => item.value) } : {},
        ...formValues['Reimbs Date From']
          ? { 'Reimbs Date From': formValues['Reimbs Date From'] } : {},
        ...formValues['Reimbs Date From'] === ''
          ? { 'Reimbs Date From': null } : {},
        ...formValues['Reimbs Date To']
          ? { 'Reimbs Date To': formValues['Reimbs Date To'] } : {},
        ...formValues['Reimbs Date To'] === ''
          ? { 'Reimbs Date To': null } : {},
        ...formValues['Report State']
          ? { 'Report State': formValues['Report State'].map(item => item.value) } : {},
        ...formValues['Report Status']
          ? { 'Report Status': formValues['Report Status'].map(item => item.value) } : {},
        ...formValues['Report Order Line Type']
          ? { 'Report Order Line Type': formValues['Report Order Line Type'].map(item => item.value) } : {},
        ...formValues['HCPC Code']
          ? { 'HCPC S Code ID': formValues['HCPC Code'].map(item => item.value) } : {},
        ...formValues['Assigned User']
          ? { 'Assigned User ID': formValues['Assigned User'].map(item => item.value) } : {},
        ...formValues.Prescriber
          ? { 'Prescriber ID': formValues.Prescriber.map(item => item.value) } : {},
        ...formValues['Sales Representative']
          ? { 'Sales Representative ID': formValues['Sales Representative'].map(item => item.value) } : {},
        ...formValues['Referral Source']
          ? { 'Referral Source ID': formValues['Referral Source'].map(item => item.value) } : {},
        ...formValues['Insurance Info']
          ? { 'Insurance Info ID': formValues['Insurance Info'].map(item => item.value) } : {},
        ...formValues.Patient
          ? { 'Patient ID': formValues.Patient.map(item => item.value) } : {},
        ...formValues.Rejected && formValues['Not Rejected']
          ? { 'Report Order Line Rejected': [true, false] } : {},
        ...formValues.Rejected && !formValues['Not Rejected']
          ? { 'Report Order Line Rejected': [true] } : {},
        ...formValues['Check Book Sheet']
          ? { 'Check Book Sheet ID': formValues['Check Book Sheet'].map(item => item.value) } : {},
      });

      if (normalizedValues['Date of Service From']) {
        normalizedValues['Date of Service From'] =
          moment(normalizedValues['Date of Service From'], dateFormat).format(dateFormat);
      }

      if (normalizedValues['Date of Service To']) {
        normalizedValues['Date of Service To'] =
          moment(normalizedValues['Date of Service To'], dateFormat).format(dateFormat);
      }

      if (normalizedValues['Date of Creation From']) {
        normalizedValues['Date of Creation From'] =
          moment(normalizedValues['Date of Creation From'], dateFormat).format(dateFormat);
      }

      if (normalizedValues['Date of Creation To']) {
        normalizedValues['Date of Creation To'] =
          moment(normalizedValues['Date of Creation To'], dateFormat).format(dateFormat);
      }

      if (normalizedValues['Reimbs Date From']) {
        normalizedValues['Reimbs Date From'] =
          moment(normalizedValues['Reimbs Date From'], dateFormat).format(dateFormat);
      }

      if (normalizedValues['Reimbs Date To']) {
        normalizedValues['Reimbs Date To'] =
          moment(normalizedValues['Reimbs Date To'], dateFormat).format(dateFormat);
      }

      delete normalizedValues['HCPC Code'];
      delete normalizedValues['Assigned User'];
      delete normalizedValues.Prescriber;
      delete normalizedValues['Sales Representative'];
      delete normalizedValues['Referral Source'];
      delete normalizedValues['Insurance Info'];
      delete normalizedValues.Patient;
      delete normalizedValues.Rejected;
      delete normalizedValues['Not Rejected'];
      delete normalizedValues['Workflow State'];
      delete normalizedValues['Workflow Status'];
      delete normalizedValues['Check Book Sheet'];
      delete normalizedValues['Default Report Structure Name'];
      delete normalizedValues['Selected Default Report Structure Name'];
      if (copyAndSaveAsNew) {
        delete normalizedValues.ID;
      }

      return normalizedValues;
    };

    if (props.variant === 'edit' && !copyAndSaveAsNew) {
      const diffValues = objectDiff(values, props.initialValues);
      return new Promise((resolve, reject) => {
        dispatch(updateReport({
          values: {
            ID: props.initialValues.ID,
            ...normalizeValues({
              ...diffValues,
              ...diffValues['Workflow State'] ? { 'Workflow State': values['Workflow State']} : {},
              ...diffValues['HCPC Code'] ? { 'HCPC Code': values['HCPC Code'] } : {},
              ...diffValues['Assigned User'] ? {
                'Assigned User': values['Assigned User'].filter(item => item),
              } : {},
              ...diffValues.Prescriber
                ? { Prescriber: values.Prescriber.filter(item => item) } : {},
              ...diffValues['Sales Representative']
                ? { 'Sales Representative': values['Sales Representative'] } : {},
              ...diffValues['Referral Source']
                ? { 'Referral Source': values['Referral Source'] } : {},
              ...diffValues['Insurance Info']
                ? { 'Insurance Info': values['Insurance Info'] } : {},
              ...diffValues.Patient ? { Patient: values.Patient } : {},
              ...diffValues['Date of Service From'] || diffValues['Date of Service To'] ? {
                'Date of Service From': values['Date of Service From'],
                'Date of Service To': values['Date of Service To'],
              } : {},
              ...diffValues['Date of Creation From'] || diffValues['Date of Creation To'] ? {
                'Date of Creation From': values['Date of Creation From'],
                'Date of Creation To': values['Date of Creation To'],
              } : {},
              ...diffValues['Reimbs Date From'] || diffValues['Reimbs Date To'] ? {
                'Reimbs Date From': values['Reimbs Date From'],
                'Reimbs Date To': values['Reimbs Date To'],
              } : {},
              ...diffValues.Rejected !== undefined || diffValues['Not Rejected'] !== undefined ? {
                Rejected: values.Rejected,
                'Not Rejected': values['Not Rejected'],
              } : {},
            }),
          },
          resolve,
          reject,
        }));
      }).then(res => res,
        (err) => {
          if (err.error) {
            throw new SubmissionError({ _error: err.error });
          }
          throw new SubmissionError(err);
        });
    }
    return new Promise((resolve, reject) => {
      dispatch(createReport({
        values: normalizeValues({
          ...values,
          ...copyAndSaveAsNew && copyValues && copyValues.Name ? {
            Name: copyValues.Name,
          } : {},
        }),
        resolve,
        reject,
      }));
    }).then(res => ({
      ...res,
      copyAndSaveAsNew,
    }),
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
  };
}

function onChange(values, dispatch, props, prevValues) {
  if (values['Report Template ID'] && values['Selected Report Template Name']
    && values['Selected Report Template Name'] === values['Report Template Name']) {
    props.onReportTemplateSelect({ ID: values['Report Template ID'] });
  }
  if (values['Workflow State'] && values['Workflow Status']
    && values['Workflow State'].map(item => item.value).join('-')
      !== prevValues['Workflow State'].map(item => item.value).join('-')) {
    props.clearFields(false, false, 'Workflow Status');
  }
  if (values['Selected Workflow State']) {
    if (!values['Workflow State']
      || values['Workflow State'].findIndex(item =>
        item.value === values['Selected Workflow State'].value) === -1) {
      props.array.push('Workflow State', values['Selected Workflow State']);
    }
    props.clearFields(false, false, 'Selected Workflow State');
  }
  if (values['Selected Workflow Status']) {
    if (!values['Workflow Status']
      || values['Workflow Status'].findIndex(item =>
        item.value === values['Selected Workflow Status'].value) === -1) {
      props.array.push('Workflow Status', values['Selected Workflow Status']);
    }
    props.clearFields(false, false, 'Selected Workflow Status');
  }
  if (values['Selected HCPC Code']) {
    if (!values['HCPC Code']
      || values['HCPC Code'].findIndex(item =>
        item.value === values['Selected HCPC Code'].value) === -1) {
      props.array.push('HCPC Code', values['Selected HCPC Code']);
    }
    props.clearFields(false, false, 'Selected HCPC Code');
  }
  if (values['Selected Report Order Line Type']) {
    if (!values['Report Order Line Type']
      || values['Report Order Line Type'].findIndex(item =>
        item.value === values['Selected Report Order Line Type'].value) === -1) {
      props.array.push('Report Order Line Type', values['Selected Report Order Line Type']);
    }
    props.clearFields(false, false, 'Selected Report Order Line Type');
  }
  if (values['Selected Assigned User']) {
    if (!values['Assigned User']
      || values['Assigned User'].findIndex(item =>
        item.value === values['Selected Assigned User'].value) === -1) {
      props.array.push('Assigned User', values['Selected Assigned User']);
    }
    props.clearFields(false, false, 'Selected Assigned User');
  }
  if (values['Selected Prescriber']) {
    if (!values.Prescriber
      || values.Prescriber.findIndex(item =>
        item.value === values['Selected Prescriber'].value) === -1) {
      props.array.push('Prescriber', values['Selected Prescriber']);
    }
    props.clearFields(false, false, 'Selected Prescriber');
  }
  if (values['Selected Sales Representative']) {
    if (!values['Sales Representative']
      || values['Sales Representative'].findIndex(item =>
        item.value === values['Selected Sales Representative'].value) === -1) {
      props.array.push('Sales Representative', values['Selected Sales Representative']);
    }
    props.clearFields(false, false, 'Selected Sales Representative');
  }
  if (values['Selected Referral Source']) {
    if (!values['Referral Source']
      || values['Referral Source'].findIndex(item =>
        item.value === values['Selected Referral Source'].value) === -1) {
      props.array.push('Referral Source', values['Selected Referral Source']);
    }
    props.clearFields(false, false, 'Selected Referral Source');
  }
  if (values['Selected Insurance Info']) {
    if (!values['Insurance Info']
      || values['Insurance Info'].findIndex(item =>
        item.value === values['Selected Insurance Info'].value) === -1) {
      props.array.push('Insurance Info', values['Selected Insurance Info']);
    }
    props.clearFields(false, false, 'Selected Insurance Info');
  }
  if (values['Selected Patient']) {
    if (!values.Patient
      || values.Patient.findIndex(item =>
        item.value === values['Selected Patient'].value) === -1) {
      props.array.push('Patient', values['Selected Patient']);
    }
    props.clearFields(false, false, 'Selected Patient');
  }
  if (values['Selected Check Book Sheet']) {
    if (!values['Check Book Sheet']
      || values['Check Book Sheet'].findIndex(item =>
        item.value === values['Selected Check Book Sheet'].value) === -1) {
      props.array.push('Check Book Sheet', values['Selected Check Book Sheet']);
    }
    props.clearFields(false, false, 'Selected Check Book Sheet');
  }

  if (values['Default Report Structure ID'] && 
    values['Selected Default Report Structure Name'] &&
    values['Selected Default Report Structure Name'] !== values['Default Report Structure Name']) {
    dispatch(props.change('Default Report Structure ID', null));
  }
}

const validateForm = (values, props) => {
  const errors = {};

  // Untill this will be maerged https://github.com/indacloud/redux-form/pull/1
  if (values['Date of Service From'] && validate.date(values['Date of Service From'])) {
    errors['Date of Service From'] = validate.date(values['Date of Service From']);
  }
  if (values['Date of Service To'] && validate.date(values['Date of Service To'])) {
    errors['Date of Service To'] = validate.date(values['Date of Service To']);
  }
  if (values['Date of Service From'] && !values['Date of Service To']) {
    errors['Date of Service To'] = 'You can add or remove only both dates, not one';
  }
  if (values['Date of Service To'] && !values['Date of Service From']) {
    errors['Date of Service To'] = 'You can add or remove only both dates, not one';
  }

  if (values['Date of Creation From'] && validate.date(values['Date of Creation From'])) {
    errors['Date of Creation From'] = validate.date(values['Date of Creation From']);
  }
  if (values['Date of Creation To'] && validate.date(values['Date of Creation To'])) {
    errors['Date of Creation To'] = validate.date(values['Date of Creation To']);
  }
  if (values['Date of Creation From'] && !values['Date of Creation To']) {
    errors['Date of Creation To'] = 'You can add or remove only both dates, not one';
  }
  if (values['Date of Creation To'] && !values['Date of Creation From']) {
    errors['Date of Creation To'] = 'You can add or remove only both dates, not one';
  }

  if (values['Reimbs Date From'] && validate.date(values['Reimbs Date From'])) {
    errors['Reimbs Date From'] = validate.date(values['Reimbs Date From']);
  }
  if (values['Reimbs Date To'] && validate.date(values['Reimbs Date To'])) {
    errors['Reimbs Date To'] = validate.date(values['Reimbs DateTo']);
  }
  if (values['Reimbs Date From'] && !values['Reimbs Date To']) {
    errors['Reimbs Date To'] = 'You can add or remove only both dates, not one';
  }
  if (values['Reimbs Date To'] && !values['Reimbs Date From']) {
    errors['Reimbs Date To'] = 'You can add or remove only both dates, not one';
  }

  if (values['Date of Service From']
    && !validate.date(values['Date of Service From'])
    && values['Date of Service To']
    && !validate.date(values['Date of Service To'])
    && moment(values['Date of Service To'], dateFormat)
      .isBefore(moment(values['Date of Service From'], dateFormat))) {
    errors['Date of Service To'] = 'Date of Service To should be greater or equal Date of Service From';
  }

  if (values['Date of Creation From']
    && !validate.date(values['Date of Creation From'])
    && values['Date of Creation To']
    && !validate.date(values['Date of Creation To'])
    && moment(values['Date of Creation To'], dateFormat)
      .isBefore(moment(values['Date of Creation From'], dateFormat))) {
    errors['Date of Creation To'] = 'Date of Creation To should be greater or equal Date of Creation From';
  }

  if (values['Reimbs Date From']
    && !validate.date(values['Reimbs Date From'])
    && values['Reimbs Date To']
    && !validate.date(values['Reimbs Date To'])
    && moment(values['Reimbs Date To'], dateFormat)
      .isBefore(moment(values['Reimbs Date From'], dateFormat))) {
    errors['Reimbs Date To'] = 'Reimbs Date To should be greater or equal Reimbs Date From';
  }

  if (values['Claim Date of Creation From']) {
    errors['Claim Date of Creation From'] = validate.date(values['Claim Date of Creation From']);
  }
  if (values['Claim Date of Creation To']) {
    errors['Claim Date of Creation To'] = validate.date(values['Claim Date of Creation To']);
  }
  if (values['Claim Date of Creation From'] && !values['Claim Date of Creation To']) {
    errors['Claim Date of Creation To'] = 'You can add or remove only both dates, not one';
  }
  if (values['Claim Date of Creation To'] && !values['Claim Date of Creation From']) {
    errors['Claim Date of Creation To'] = 'You can add or remove only both dates, not one';
  }

  if (values['Claim Date of Creation From']
    && !validate.date(values['Claim Date of Creation From'])
    && values['Claim Date of Creation To']
    && !validate.date(values['Claim Date of Creation To'])
    && moment(values['Claim Date of Creation To'], dateFormat)
      .isBefore(moment(values['Claim Date of Creation From'], dateFormat))) {
    errors['Claim Date of Creation To'] = 'Claim Date of Creation To should be greater or equal Claim Date of Creation From';
  }

  if (props.variant === 'edit') {
    // eslint-disable-next-line no-underscore-dangle
    errors._error = 'Filters not selected';

    if (values['Date of Service From']
      || values['Date of Service To']
      || values['Date of Creation From']
      || values['Date of Creation To']
      || (values['Workflow State'] && values['Workflow State'].length > 0)
      || (values['Workflow Status'] && values['Workflow Status'].length > 0)
      || values['Reimbs Date From']
      || values['Reimbs Date To']
      || (values['Report State'] && values['Report State'].length > 0)
      || (values['Report Status'] && values['Report Status'].length > 0)
      || (values['HCPC Code'] && values['HCPC Code'].length > 0)
      || (values['Report Order Line Type'] && values['Report Order Line Type'].length > 0)
      || (values['Assigned User'] && values['Assigned User'].length > 0)
      || (values.Prescriber && values.Prescriber.length > 0)
      || (values['Sales Representative'] && values['Sales Representative'].length > 0)
      || (values['Referral Source'] && values['Referral Source'].length > 0)
      || (values['Insurance Info'] && values['Insurance Info'].length > 0)
      || (values.Patient && values.Patient.length > 0)
      || (values.Rejected === true)
      || (values['Check Book Sheet'] && values['Check Book Sheet'].length > 0)) {
      // eslint-disable-next-line no-underscore-dangle
      delete errors._error;
    }
  }
  return errors;
};

function onSubmitFail(errors) {
  const selectorString = Object.keys(errors)
    .reduce((accumulator, currentValue) => [
      ...accumulator,
      `[name="${currentValue}"]`,
    ], []).join(', ');
  const firstErrorEl = selectorString ? document.querySelector(selectorString) : null;
  if (firstErrorEl) {
    firstErrorEl.focus();
    const top = firstErrorEl.getBoundingClientRect().top;
    if (top < 100) {
      const scrollTop = window.pageYOffset
        || (document.documentElement || document.body.parentNode || document.body).scrollTop;
      window.scrollTo(0, (top + scrollTop) - 100);
    }
  }
}

const ReportsCreateEditForm = ({
  handleSubmit,
  variant,
  classes,
  ...otherProps
}) => (
  <Fragment>
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit(false))}
      autoComplete="off"
    >
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        {variant !== 'insurance' &&
          <Grid item xs={12} md={8}>
            <ReportsCreateEditFormAside
              variant={variant}
              handleSubmit={handleSubmit}
              onSubmitCustom={onSubmit}
              {...otherProps}
            />
          </Grid>
        }
        <Grid item xs={12} md={variant === 'insurance' ? 12 : 4}>
          <ReportsCreateEditFormMain
            variant={variant}
            handleSubmit={handleSubmit}
            onSubmitCustom={onSubmit}
            {...otherProps}
          />
        </Grid>
      </Grid>
    </form>
  </Fragment>
);

ReportsCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['create', 'edit', 'claim', 'insurance']).isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

export default reduxForm({
  onChange,
  // onSubmit,
  onSubmitFail,
  validate: validateForm,
  enableReinitialize: true,
})(withStyles(styles)(ReportsCreateEditForm));



// WEBPACK FOOTER //
// ./src/components/reports/ReportsCreateEditForm/ReportsCreateEditForm.js